import React, { useState } from "react";
import cn from "classnames";

import { User } from "../../types";
import { ArrowDownShape, ArrowUpShape, ArrowRightShape, CloseIcon, Hamburger, UserIcon } from "../Icons";
import { UserMenu } from "./UserMenu";
import ComposableHeaderDropdown, { ComposableHeaderDropdownProviderProps } from "../Dropdowns/ComposableHeaderDropdown";
import { MuseColorLogo } from "../Logos";
import JobSearchInput from "./JobSearchInput";
import { navItems, NavItem } from "./navItems";

import styles from "./Header.module.scss";
import headerDropdownClasses from "./HeaderSearchDropdown.module.scss";

interface HeaderProps {
    activePath?: string;
    mainContainerId?: string;
    user?: User;
    onSignIn?: () => void;
    onSignOut?: () => void;
    className?: string;
    enableSearch?: boolean;
    enableCompanySearch?: boolean;
    companySearchDropdownProps?: ComposableHeaderDropdownProviderProps;
    searchUrl?: string;
    onSearchSubmit?: (keyword: string) => void;
    staticMobileHeader?: boolean;
    showSubMenus?: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    showRebrand?: boolean;
}

// the most common user is not logged in. Someday we hope this changes, but it is what Google bot would see.
const defaultUser = {
    logged_in: false,
    view_bookings: false,
    company: null,
    location: null,
    cropped_name: "",
    email: "",
    favorite_posts: [],
    name: "",
    id: null
};

const mouseUpHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.currentTarget.blur();
    event.stopPropagation();
};

/**
 * The User Trigger shows a user icon when the user is logged in with functions to show the user panel.
 * When the user is not logged in, shows a link to the login page.
 */
const UserTrigger = (props: {
    handleClickSignIn?: () => void,
    mouseUpHandler?: (event: React.MouseEvent<HTMLElement>) => void,
    toggleUserMenu?: () => void,
    user?: User,
}): React.ReactElement | null => {
    const {
        handleClickSignIn,
        mouseUpHandler,
        toggleUserMenu,
        user = defaultUser,
    } = props;
    return (
        <div className={styles.navItem}>
            {user?.logged_in
                ? (
                    <button
                    className={cn(styles.navButton, styles.userButton)}
                    type="button"
                    aria-label="Open User Menu"
                    aria-haspopup="true"
                    onClick={toggleUserMenu}
                    onMouseUp={mouseUpHandler}
                    >
                        <UserIcon />
                    </button>
                )
                : (
                    <a
                    tabIndex={0}
                    className={cn(styles.signIn, styles.menuItemLink)}
                    aria-label="Sign In"
                    href="/user/login"
                    onClick={handleClickSignIn}
                    onMouseUp={mouseUpHandler}
                    >
                        Sign In
                    </a>
                )
            }
        </div>
    );
};

/**
 * The Hamburger Trigger shows a hamburger icon (only in mobile) with functions to show the global panel.
 */
const HamburgerTrigger = (props: {
    mouseUpHandler?: (event: React.MouseEvent<HTMLElement>) => void,
    toggleHamburgerMenu?: () => void,
}): React.ReactElement | null => {
    const {
        mouseUpHandler,
        toggleHamburgerMenu,
    } = props;
    return (
        <div className={styles.navItem}>
            <button
                type="button"
                className={cn(styles.navButton, styles.hamburgerButton)}
                aria-label="Open Navigation Menu"
                aria-haspopup="true"
                onClick={toggleHamburgerMenu}
                onMouseUp={mouseUpHandler}
            >
                <Hamburger />
            </button>
        </div>
    );
};

const SubNav = ({ outlined, text, subNavItems }: NavItem) => {
    const [isOpen, setIsOpen] = React.useState(false);
    
    return (
    <>
        <div
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            className={styles.trigger}
        />
        <details className={ cn(styles.subMenu, { [styles.outlined]: outlined })} open={isOpen}>
            <summary className={styles.menuItem} aria-label={`Open ${text} Sub Menu`}>
                <span className={styles.menuItemLink}>{ text }</span>
                <ArrowUpShape className={styles.arrowUp} />
                <ArrowDownShape className={styles.arrowDown} />
            </summary>
            <ul
                className={styles.detailsList}
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
            >
                { subNavItems && subNavItems.map(({ href, text, dataHeaderLink, showOnMobile }) => (
                    <li key={ dataHeaderLink } className={cn(styles.subMenuItem, {[styles.hiddenMobile]: !showOnMobile})}>
                        <a
                            className={ cn(styles.menuItemLink, styles.subMenuItemLink) }
                            href={href}
                        >
                            { text }
                        </a>
                    </li>
                )) }
                <li key="view-all-jobs" className={styles.subMenuItem}>
                    <a
                        className={ cn(styles.menuItemLink, styles.subMenuItemLink) }
                        href="/jobs"
                    >
                        View All Jobs <ArrowRightShape />
                    </a>
                </li>
            </ul>
        </details>
    </>
)};

const Header = ({
    activePath = "",
    className,
    mainContainerId,
    user,
    searchUrl,
    onSignIn = () => {},
    onSignOut = () => {},
    enableSearch = false,
    enableCompanySearch = false,
    companySearchDropdownProps,
    onSearchSubmit,
    staticMobileHeader = false,
    showSubMenus = false
}: HeaderProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const dropdownClasses = {
        container: `${headerDropdownClasses.container}`,
        containerOpened: `${headerDropdownClasses.containerOpened}`,
        inputContainer: headerDropdownClasses.inputContainer,
        inputContainerOpened: headerDropdownClasses.inputContainerOpened,
        input: headerDropdownClasses.input,
        menuOpened: headerDropdownClasses.menuOpened,
        menuList: headerDropdownClasses.menuList,
        listItem: headerDropdownClasses.listItem,
        listItemText: headerDropdownClasses.listItemText,
        closeButton: headerDropdownClasses.closeButton,
        inputClearButton: headerDropdownClasses.inputClearButton,
        clearButton: headerDropdownClasses.clearButton,
        postLabel: headerDropdownClasses.postLabel,
    };
    const companySearchProps = {
        classes: dropdownClasses,
        dropdownProps: companySearchDropdownProps || {
            inputId: "search-input",
            dropdownList: []
        }
    };

    return (
        <header className={cn(styles.header, {[styles.staticMobile]: staticMobileHeader}, className)}>
                <a
                    tabIndex={0}
                    aria-label="Skip to main content"
                    className={styles.skipNav}
                    href={(mainContainerId?.[0] !== "#" ? "#" : "") + mainContainerId}
                >
                    Skip to main content
                </a>
                <a
                className={styles.logoAnchor}
                href="/"
                aria-label="Home Page"
                onMouseUp={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    e.currentTarget.blur();
                }}
                >
                    <MuseColorLogo className={styles.logo} />
                </a>
                { enableSearch ? (
                    <JobSearchInput
                    className={styles.jobSearch}
                        searchUrl={searchUrl ?? ""}
                        onSearchSubmit={onSearchSubmit}
                    />
                ) : null }
                { enableCompanySearch && companySearchDropdownProps
                ? (
                    <ComposableHeaderDropdown {...companySearchProps} />
                ) : null }
                <nav className={styles.navItems}>
                    <HamburgerTrigger toggleHamburgerMenu={() => setMenuOpen(true)}/>
                    <ul className={cn(styles.menu, styles.mainMenu, { [styles.open]: menuOpen })}>
                        { navItems.map(({text, href, outlined, dataHeaderLink, subNavItems}: NavItem, index) => {
                            const hasSubNavItems = showSubMenus && subNavItems && subNavItems.length;
                            return (
                                <li
                                    className={ cn({
                                        [styles.menuItem]: !hasSubNavItems,
                                        [styles.outlined]: outlined,
                                        [styles.menuItemSubNav]: hasSubNavItems
                                    })}
                                    key={ dataHeaderLink }
                                >
                                    { hasSubNavItems ? (
                                        <SubNav {...{...navItems[index]}} />
                                    ) : (
                                        <a
                                            className={ cn(styles.menuItemLink, { [styles.active]: activePath?.includes(href) }) }
                                            href={ href }
                                        >
                                            <div>{ text }</div>
                                        </a>
                                    )}
                                </li>
                            )
                        }) }
                    </ul>
                    <UserTrigger
                        user={user}
                        toggleUserMenu={() => setUserMenuOpen(true)}
                        handleClickSignIn={onSignIn}
                    />
                    <UserMenu
                        user={user}
                        menuOpen={userMenuOpen}
                        onSignOut={onSignOut}
                    />
                    <button
                        className={cn(styles.closeButton, { [styles.open]: menuOpen || userMenuOpen }, { [styles.mainMenuOpen] : menuOpen })}
                        tabIndex={0}
                        type="button"
                        aria-label="Close Navigation Menu"
                        onClick={() => {
                            menuOpen && setMenuOpen(false);
                            userMenuOpen && setUserMenuOpen(false);
                        }}
                        onMouseUp={mouseUpHandler}
                    >
                        <CloseIcon />
                    </button>
                </nav>
        </header>
    );
};

export {
    Header as default
};
export type { HeaderProps };
